import React from 'react';
import styled from 'styled-components';
import { useStoreState, useStoreActions } from '../store';
import { calculateDistance } from '../utils/calculate-distance';
import { theme } from '../utils/theme';
import { breakpoints } from '../utils/breakpoints';
import { isSmartTV } from '../utils/tv';
import { Browser } from '../utils/browser';
import volumeOnIcon from '../assets/images/volume-on.svg';
import volumeOffIcon from '../assets/images/volume-off.svg';

export const VolumeControl = () => {
  const containerElementRef = React.useRef<HTMLDivElement | null>(null);
  const thumbnailElementRef = React.useRef<HTMLDivElement | null>(null);
  const progressElementRef = React.useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const volume = useStoreState((state) => state.player.volume);
  const muted = useStoreState((state) => state.player.muted);
  const videoElement = useStoreState((state) => state.player.videoElement);
  const castSession = useStoreState((state) => state.chromecast.castSession);
  const playerActions = useStoreActions((actions) => actions.player);
  const chromecastActions = useStoreActions((actions) => actions.chromecast);

  function handleMuteControlClick() {
    muted ? playerActions.unmute() : playerActions.mute();

    if (castSession) {
      chromecastActions.muteOrUnmute();
    }
  }

  function handleMouseDown(e: MouseEvent | TouchEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    handleWindowMouseMove(e as MouseEvent);
    document.addEventListener('mousemove', handleWindowMouseMove);
    document.addEventListener('touchmove', handleWindowMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchend', handleMouseUp);
  }

  function handleMouseUp(e: MouseEvent | TouchEvent) {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleWindowMouseMove);
    document.removeEventListener('touchmove', handleWindowMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('touchend', handleMouseUp);

    const offset = calculateDistance(e, containerElementRef.current);

    playerActions.setVolume(offset);

    if (castSession) {
      chromecastActions.setVolume(offset / 100);
    }
  }

  function handleWindowMouseMove(e: MouseEvent | TouchEvent) {
    const offset = calculateDistance(e, containerElementRef.current);
    updateProgress(offset);
    updateThumbnailPosition(offset);

    if (videoElement) {
      playerActions.setVolume(offset);
    }
  }

  function updateThumbnailPosition(offset: number) {
    const { current: thumbnailElement } = thumbnailElementRef;
    if (!thumbnailElement) return;
    thumbnailElement.style.left = `${offset}%`;
  }

  function updateProgress(offset: number) {
    const { current: progressElement } = progressElementRef;
    if (!progressElement) return;
    progressElement.style.width = `${offset}%`;
  }

  function isMobileDevice() {
    return window.innerWidth < breakpoints.large || Browser.isIOS || Browser.isAndroid;
  }

  /**
   * Update timeline progress from application state,
   * unless the user is dragging the timeline control.
   */
  React.useEffect(() => {
    if (isDragging) return;
    updateThumbnailPosition(volume);
    updateProgress(volume);
  }, [isDragging, volume]);

  if (isSmartTV()) {
    return null;
  }

  return (
    <Container data-testid="volumeControl">
      <MuteControl data-testid="muteControl" muted={muted} onClick={handleMuteControlClick} />
      {isMobileDevice() ? null : (
        <VolumeBarContainer>
          <VolumeBar
            data-testid="volumeBarControl"
            className="volume"
            ref={containerElementRef}
            onMouseDown={(e) => handleMouseDown(e.nativeEvent)}
            onTouchStart={(e) => handleMouseDown(e.nativeEvent)}
          >
            <div className="volume-controls">
              <div className="track-bar" />
              <div className="progress-bar" ref={progressElementRef} />
              <div className="thumb" ref={thumbnailElementRef} />
            </div>
          </VolumeBar>
        </VolumeBarContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
`;

export const BaseControl = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s ease-out;

  @media ${theme.mobileMediaQuery} {
    width: 18px;
    height: 18px;
  }
`;

const MuteControl = styled(BaseControl)<{ muted: boolean }>`
  background: url(${({ muted }) => (muted ? volumeOffIcon : volumeOnIcon)}) no-repeat center / contain;
`;

const VolumeBarContainer = styled(BaseControl)`
  width: 88px;
  padding-left: 16px;
  opacity: 1;

  @media ${theme.mobileMediaQuery} {
    display: none;
  }
`;

const VolumeBar = styled.div`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  position: relative;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  .volume-controls {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .track-bar {
      width: 100%;
      height: 3px;
      background-color: ${({ theme }) => theme.lighterGray};
    }

    .progress-bar {
      width: 0%;
      height: 1px;
      position: absolute;
      border: none;
      border-radius: 3px;
      top: 50%;
      margin: -0.5px 0 0 0;
    }

    .thumb {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: ${({ theme }) => theme.lighterGray};
      position: absolute;
      top: 50%;
      margin-top: -5px;
      margin-left: -5px;
    }
  }
`;
